import React, { useState, useEffect, useMemo, type ReactNode } from 'react';
import type { Event, EventMealSlot, Reservation, Table } from '../types';
import { eventsService, reservationsService, tablesService } from '../lib/database';
import {
  getTotalAvailableCapacity,
  getMaxSingleTableCapacity,
  isBuffetMultiSlotEvent,
  getEventBookingDates,
  evaluateBuffetBookingCapacity,
} from '../lib/reservationCapacity';
import { formatIsoDateDdMmYyyy } from '../lib/formatDisplayDate';

interface PublicBookingPageProps {
  eventId: string;
  businessName?: string;
  /** From Settings → Business info → Logo URL */
  logoUrl?: string;
}

function BookingHeroPhoto({ src }: { src: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div
        className="absolute inset-0 bg-gradient-to-br from-pink-600 via-pink-500 to-rose-600"
        aria-hidden
      />
    );
  }
  return (
    <img
      src={src}
      alt=""
      width={1600}
      height={600}
      className="absolute inset-0 h-full w-full object-cover object-center"
      loading="eager"
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
}

function BookingHeaderLogo({ src, alt }: { src: string; alt: string }) {
  const [visible, setVisible] = useState(true);
  if (!visible) return null;
  return (
    <img
      src={src}
      alt={alt}
      onError={() => setVisible(false)}
      className="absolute top-4 right-4 z-10 h-12 sm:h-14 w-auto max-w-[min(46vw,200px)] object-contain rounded-md bg-white/95 px-2 py-1.5 shadow-lg ring-1 ring-black/10"
    />
  );
}

function PublicBookingHero({
  event,
  logoUrl,
  businessName,
  children,
}: {
  event: Event;
  logoUrl?: string;
  businessName?: string;
  children: ReactNode;
}) {
  const heroSrc = event.bookingHeroImageUrl?.trim() ?? '';
  const hasPhoto = Boolean(heroSrc);
  const showLogo = Boolean(logoUrl?.trim());
  const logoAlt = businessName ? `${businessName} logo` : 'Restaurant logo';
  return (
    <div
      className={`relative w-full overflow-hidden text-white ${
        hasPhoto
          ? 'min-h-[220px] sm:min-h-[280px]'
          : 'min-h-[140px] bg-gradient-to-br from-pink-600 via-pink-500 to-rose-600 px-6 py-8'
      }`}
    >
      {hasPhoto ? (
        <>
          <BookingHeroPhoto src={heroSrc} />
          <div className="absolute inset-0 bg-black/10 pointer-events-none" aria-hidden />
        </>
      ) : null}
      {showLogo ? <BookingHeaderLogo src={logoUrl!.trim()} alt={logoAlt} /> : null}
      <div
        className={`relative z-[1] max-w-2xl pr-4 sm:pr-44 ${
          hasPhoto
            ? 'm-4 rounded-xl bg-black/45 px-4 py-4 backdrop-blur-[2px] ring-1 ring-white/15 sm:m-6'
            : 'drop-shadow-md'
        }`}
      >
        {children}
      </div>
    </div>
  );
}

function buffetSlotsForEvent(event: Event): EventMealSlot[] {
  if (event.mealSlots?.length) return event.mealSlots;
  if (isBuffetMultiSlotEvent(event)) {
    return [
      {
        id: 'default',
        label: 'Seating',
        startTime: event.startTime,
        endTime: event.endTime,
      },
    ];
  }
  return [];
}

const PublicBookingPage: React.FC<PublicBookingPageProps> = ({
  eventId,
  businessName = 'Kings Park',
  logoUrl,
}) => {
  const [event, setEvent] = useState<Event | null>(null);
  const [tables, setTables] = useState<Table[]>([]);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [form, setForm] = useState({
    adultsCount: '1',
    kidsCount: '0',
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    reservationTime: '',
    specialRequests: '',
  });

  const [buffetDate, setBuffetDate] = useState('');
  const [buffetSlotId, setBuffetSlotId] = useState('');
  const [buffetAdultsBand, setBuffetAdultsBand] = useState<string>('1');
  const [buffetAdultsTenPlus, setBuffetAdultsTenPlus] = useState('11');
  const [buffetKidsBelow10, setBuffetKidsBelow10] = useState('0');
  const [buffetKidsTenPlus, setBuffetKidsTenPlus] = useState('0');
  const [foodAllergy, setFoodAllergy] = useState<'none' | 'has_allergies'>('none');
  const [allergyDetails, setAllergyDetails] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [ev, tbls, allRes] = await Promise.all([
          eventsService.getById(eventId),
          tablesService.getAll(),
          reservationsService.getAll(),
        ]);
        if (cancelled) return;
        if (!ev || ev.deleted) {
          setError('Event not found.');
          setEvent(null);
          setTables([]);
          setReservations([]);
          return;
        }
        setEvent(ev);
        setTables(tbls);
        setReservations(allRes);
        const dates = getEventBookingDates(ev);
        const slots = buffetSlotsForEvent(ev);
        setBuffetDate((d) => (d && dates.includes(d) ? d : dates[0] ?? ''));
        setBuffetSlotId((sid) => {
          if (sid && slots.some((s) => s.id === sid)) return sid;
          return slots[0]?.id ?? '';
        });
        if (!form.reservationTime) setForm((f) => ({ ...f, reservationTime: ev.startTime }));
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load event.');
          setEvent(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const buffetMode = event != null && isBuffetMultiSlotEvent(event);
  const bookingDates = useMemo(() => (event ? getEventBookingDates(event) : []), [event]);
  const mealSlots = useMemo(() => (event ? buffetSlotsForEvent(event) : []), [event]);

  const selectedSlot = useMemo(
    () => mealSlots.find((s) => s.id === buffetSlotId) ?? mealSlots[0] ?? null,
    [mealSlots, buffetSlotId]
  );

  const reservationDateStandard = event?.eventDate ?? '';
  const reservationTimeStandard = form.reservationTime || event?.startTime || '18:00';
  const adultsNum = parseInt(form.adultsCount, 10) || 0;
  const kidsNum = parseInt(form.kidsCount, 10) || 0;
  const partySizeStandard = adultsNum + kidsNum;

  const buffetAdultsNum = useMemo(() => {
    if (buffetAdultsBand === '10plus') {
      const n = parseInt(buffetAdultsTenPlus.replace(/\D/g, ''), 10) || 11;
      return Math.max(11, n);
    }
    return Math.min(10, Math.max(1, parseInt(buffetAdultsBand, 10) || 1));
  }, [buffetAdultsBand, buffetAdultsTenPlus]);

  const buffetKidsBelowN = Math.min(10, Math.max(0, parseInt(buffetKidsBelow10, 10) || 0));
  const buffetKidsAboveN = Math.min(10, Math.max(0, parseInt(buffetKidsTenPlus, 10) || 0));
  const partySizeBuffet = buffetAdultsNum + buffetKidsBelowN + buffetKidsAboveN;

  const reservationDate = buffetMode ? buffetDate : reservationDateStandard;
  const reservationTime = buffetMode
    ? selectedSlot?.startTime ?? event?.startTime ?? '18:00'
    : reservationTimeStandard;

  const totalCapacity = useMemo(
    () =>
      event && reservationDate
        ? getTotalAvailableCapacity(
            tables,
            reservations,
            reservationDate,
            reservationTime,
            event.maxCapacity
          )
        : 0,
    [event, tables, reservations, reservationDate, reservationTime]
  );

  const maxSingleCapacity = useMemo(() => getMaxSingleTableCapacity(tables), [tables]);

  const buffetCapacity = useMemo(() => {
    if (!buffetMode || !event || !reservationDate || !selectedSlot) return null;
    return evaluateBuffetBookingCapacity(
      event,
      tables,
      reservations,
      reservationDate,
      selectedSlot.startTime,
      selectedSlot.id,
      partySizeBuffet
    );
  }, [
    buffetMode,
    event,
    tables,
    reservations,
    reservationDate,
    selectedSlot,
    partySizeBuffet,
  ]);

  const canFitTableStandard = partySizeStandard <= maxSingleCapacity;
  const canFitVenueStandard = totalCapacity >= partySizeStandard;
  const mustWaitlistStandard = !canFitVenueStandard;

  const mustWaitlistBuffet = buffetCapacity?.mustWaitlist ?? false;
  const requiresMergeBuffet = buffetCapacity?.requiresMerge ?? false;

  const useAdultsKids =
    event != null && !buffetMode && typeof event.kidsAgeUnder === 'number' && event.kidsAgeUnder > 0;

  const showTableComms = event?.showTableInCustomerComms !== false;

  const handleSubmitStandard = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!event) return;
    if (!form.customerName.trim() || !form.customerPhone.trim()) {
      setSubmitError('Please enter your name and phone number.');
      return;
    }
    const email = form.customerEmail.trim();
    if (!email) {
      setSubmitError('Email is required so we can send your confirmation.');
      return;
    }
    if (partySizeStandard < 1) {
      setSubmitError('Please enter at least 1 adult or 1 child.');
      return;
    }
    setSubmitError(null);
    try {
      const now = Date.now();
      const payload: Omit<Reservation, 'id'> = {
        tableId: 0,
        customerName: form.customerName.trim(),
        customerPhone: form.customerPhone.trim(),
        customerEmail: email,
        partySize: partySizeStandard,
        ...(useAdultsKids && { adultsCount: adultsNum, kidsCount: kidsNum }),
        reservationDate: event.eventDate,
        reservationTime: form.reservationTime,
        status: mustWaitlistStandard ? 'waitlist' : 'pending',
        specialRequests: form.specialRequests.trim() || undefined,
        eventId: event.id,
        requiresMerge: !canFitTableStandard && canFitVenueStandard,
        createdAt: now,
        updatedAt: now,
      };
      await reservationsService.create(payload);
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit. Please try again.');
    }
  };

  const handleSubmitBuffet = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!event || !selectedSlot || !buffetDate) {
      setSubmitError('Please choose a date and meal time.');
      return;
    }
    if (!form.customerName.trim() || !form.customerPhone.trim()) {
      setSubmitError('Please enter your name and mobile number.');
      return;
    }
    const email = form.customerEmail.trim();
    if (!email) {
      setSubmitError('Email is required so we can send your confirmation.');
      return;
    }
    if (partySizeBuffet < 1) {
      setSubmitError('Party size must be at least 1.');
      return;
    }
    if (foodAllergy === 'has_allergies' && !allergyDetails.trim()) {
      setSubmitError('Please describe the allergies, or choose “No known allergies”.');
      return;
    }
    setSubmitError(null);
    try {
      const now = Date.now();
      const payload: Omit<Reservation, 'id'> = {
        tableId: 0,
        customerName: form.customerName.trim(),
        customerPhone: form.customerPhone.trim(),
        customerEmail: email,
        partySize: partySizeBuffet,
        adultsCount: buffetAdultsNum,
        kidsCount: buffetKidsBelowN + buffetKidsAboveN,
        kidsBelow10Count: buffetKidsBelowN,
        kidsAbove10Count: buffetKidsAboveN,
        reservationDate: buffetDate,
        reservationTime: selectedSlot.startTime,
        slotId: selectedSlot.id,
        status: mustWaitlistBuffet ? 'waitlist' : 'pending',
        specialRequests: form.specialRequests.trim() || undefined,
        eventId: event.id,
        requiresMerge: requiresMergeBuffet,
        foodAllergyDeclaration: foodAllergy,
        allergyDetails:
          foodAllergy === 'has_allergies' ? allergyDetails.trim() : undefined,
        createdAt: now,
        updatedAt: now,
      };
      await reservationsService.create(payload);
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500" />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <h1 className="text-xl font-bold text-gray-800 mb-2">Booking unavailable</h1>
          <p className="text-gray-600">{error || 'This event could not be found.'}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    const waitlisted = buffetMode ? mustWaitlistBuffet : mustWaitlistStandard;
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-800 mb-2">Request received</h1>
          <p className="text-gray-600 mb-4">
            {waitlisted
              ? "You're on the waiting list. We'll contact you if space becomes available."
              : "Your reservation request has been sent. We'll confirm shortly."}
          </p>
          {showTableComms && !waitlisted && (
            <p className="text-sm text-gray-500 mb-2">
              Table assignments may be confirmed separately and can change for operational reasons.
            </p>
          )}
          {!showTableComms && (
            <p className="text-sm text-gray-500 mb-2">
              Seating will be arranged by our team; we won&apos;t assign a specific table until you hear from us.
            </p>
          )}
          <p className="text-sm text-gray-500">{businessName}</p>
        </div>
      </div>
    );
  }

  if (buffetMode) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-lg mx-auto">
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <PublicBookingHero event={event} logoUrl={logoUrl} businessName={businessName}>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{event.name}</h1>
              <p className="text-white/90 mt-1.5 text-sm">
                {bookingDates.length > 1
                  ? `${bookingDates.length} dates · choose yours below`
                  : `${formatIsoDateDdMmYyyy(bookingDates[0] ?? '')} · hosted booking`}
              </p>
              {event.description && (
                <p className="mt-2 text-sm text-white/85">{event.description}</p>
              )}
              {event.customerEventDetails && (
                <p className="mt-3 text-sm text-white/95 whitespace-pre-wrap border-t border-white/25 pt-3">
                  {event.customerEventDetails}
                </p>
              )}
            </PublicBookingHero>

            <form onSubmit={handleSubmitBuffet} className="p-6 space-y-4">
              {bookingDates.length > 1 ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Select date *</label>
                  <select
                    value={buffetDate}
                    onChange={(e) => setBuffetDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500"
                    required
                  >
                    {bookingDates.map((d) => (
                      <option key={d} value={d}>
                        {formatIsoDateDdMmYyyy(d)}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <div className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-lg text-gray-700">
                    {formatIsoDateDdMmYyyy(bookingDates[0] ?? '')}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Meal / sitting *</label>
                <select
                  value={buffetSlotId}
                  onChange={(e) => setBuffetSlotId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500"
                  required
                >
                  {mealSlots.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                      {s.endTime ? ` (${s.startTime}–${s.endTime})` : ` (${s.startTime})`}
                    </option>
                  ))}
                </select>
              </div>

              {event.parkingInstructions && (
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-sm text-blue-900 whitespace-pre-wrap">
                  <div className="font-medium text-blue-950 mb-1">Parking</div>
                  {event.parkingInstructions}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Adults *</label>
                <p className="text-xs text-gray-500 mb-1">Ages 10 and over (choose 1–10 or 10+)</p>
                <select
                  value={buffetAdultsBand}
                  onChange={(e) => setBuffetAdultsBand(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500"
                >
                  {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={String(n)}>
                      {n}
                    </option>
                  ))}
                  <option value="10plus">10 or more</option>
                </select>
                {buffetAdultsBand === '10plus' && (
                  <input
                    type="text"
                    inputMode="numeric"
                    className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg"
                    value={buffetAdultsTenPlus}
                    onChange={(e) => setBuffetAdultsTenPlus(e.target.value.replace(/\D/g, ''))}
                    placeholder="Number of adults (min 11)"
                  />
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kids under 10 *</label>
                  <select
                    value={buffetKidsBelow10}
                    onChange={(e) => setBuffetKidsBelow10(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    {Array.from({ length: 11 }, (_, i) => (
                      <option key={i} value={String(i)}>
                        {i}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kids 10+ *</label>
                  <select
                    value={buffetKidsTenPlus}
                    onChange={(e) => setBuffetKidsTenPlus(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    {Array.from({ length: 11 }, (_, i) => (
                      <option key={i} value={String(i)}>
                        {i}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <p className="text-xs text-gray-500">Total party size: {partySizeBuffet}</p>

              <div>
                <span className="block text-sm font-medium text-gray-700 mb-2">Food allergies *</span>
                <label className="flex items-center gap-2 mb-2">
                  <input
                    type="radio"
                    name="allergy"
                    checked={foodAllergy === 'none'}
                    onChange={() => setFoodAllergy('none')}
                  />
                  <span className="text-sm">No known allergies</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="allergy"
                    checked={foodAllergy === 'has_allergies'}
                    onChange={() => setFoodAllergy('has_allergies')}
                  />
                  <span className="text-sm">Yes — we need details</span>
                </label>
                {foodAllergy === 'has_allergies' && (
                  <textarea
                    value={allergyDetails}
                    onChange={(e) => setAllergyDetails(e.target.value)}
                    rows={2}
                    className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Please list allergens or dietary requirements"
                    required
                  />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full name *</label>
                <input
                  type="text"
                  value={form.customerName}
                  onChange={(e) => setForm((f) => ({ ...f, customerName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mobile *</label>
                <input
                  type="tel"
                  value={form.customerPhone}
                  onChange={(e) => setForm((f) => ({ ...f, customerPhone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  value={form.customerEmail}
                  onChange={(e) => setForm((f) => ({ ...f, customerEmail: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Special requests</label>
                <textarea
                  value={form.specialRequests}
                  onChange={(e) => setForm((f) => ({ ...f, specialRequests: e.target.value }))}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              {mustWaitlistBuffet && (
                <p className="text-amber-700 text-sm bg-amber-50 px-3 py-2 rounded-lg">
                  This sitting is at capacity for online requests. You can still join the waiting list and
                  we&apos;ll contact you if space opens up.
                </p>
              )}
              {!mustWaitlistBuffet && requiresMergeBuffet && (
                <p className="text-blue-700 text-sm bg-blue-50 px-3 py-2 rounded-lg">
                  We may combine tables for larger parties. Our team will confirm seating.
                </p>
              )}

              {submitError && (
                <p className="text-red-700 text-sm bg-red-50 px-3 py-2 rounded-lg">{submitError}</p>
              )}

              <button
                type="submit"
                disabled={partySizeBuffet < 1}
                className="w-full py-3 px-4 bg-pink-500 text-white font-semibold rounded-lg hover:bg-pink-600 focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {mustWaitlistBuffet ? 'Join waiting list' : 'Request reservation'}
              </button>
            </form>
          </div>
          <p className="text-center text-sm text-gray-500 mt-4">{businessName}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <PublicBookingHero event={event} logoUrl={logoUrl} businessName={businessName}>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{event.name}</h1>
            <p className="text-white/90 mt-1.5 text-sm">
              {formatIsoDateDdMmYyyy(event.eventDate)} · {event.startTime} – {event.endTime}
            </p>
            {event.description && (
              <p className="mt-2 text-sm text-white/85">{event.description}</p>
            )}
            {event.customerEventDetails && (
              <p className="mt-2 text-sm text-white/95 whitespace-pre-wrap">{event.customerEventDetails}</p>
            )}
          </PublicBookingHero>

          <form onSubmit={handleSubmitStandard} className="p-6 space-y-4">
            {useAdultsKids ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Number of adults *</label>
                  <p className="text-xs text-gray-500 mb-1">Ages {event.kidsAgeUnder} and over</p>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={form.adultsCount}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, '');
                      setForm((f) => ({ ...f, adultsCount: v }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Number of kids *</label>
                  <p className="text-xs text-gray-500 mb-1">Under {event.kidsAgeUnder} years</p>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={form.kidsCount}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, '');
                      setForm((f) => ({ ...f, kidsCount: v }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                    placeholder="0"
                  />
                </div>
                {partySizeStandard < 1 && (
                  <p className="text-amber-700 text-sm">Please enter at least 1 adult or 1 child.</p>
                )}
              </>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Party size *</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={form.adultsCount}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, '');
                    setForm((f) => ({ ...f, adultsCount: v, kidsCount: '0' }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                  placeholder="1"
                />
                {partySizeStandard < 1 && (
                  <p className="text-amber-700 text-sm mt-1">Please enter at least 1.</p>
                )}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Preferred time *</label>
              <input
                type="time"
                value={form.reservationTime}
                onChange={(e) => setForm((f) => ({ ...f, reservationTime: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Your name *</label>
              <input
                type="text"
                value={form.customerName}
                onChange={(e) => setForm((f) => ({ ...f, customerName: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                placeholder="Full name"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
              <input
                type="tel"
                value={form.customerPhone}
                onChange={(e) => setForm((f) => ({ ...f, customerPhone: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                placeholder="Contact number"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <input
                type="email"
                value={form.customerEmail}
                onChange={(e) => setForm((f) => ({ ...f, customerEmail: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                placeholder="We’ll send your confirmation here"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Special requests</label>
              <textarea
                value={form.specialRequests}
                onChange={(e) => setForm((f) => ({ ...f, specialRequests: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                placeholder="Dietary needs, accessibility, etc."
              />
            </div>

            {event.parkingInstructions && (
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-sm text-blue-900 whitespace-pre-wrap">
                <div className="font-medium text-blue-950 mb-1">Parking</div>
                {event.parkingInstructions}
              </div>
            )}

            {mustWaitlistStandard && (
              <p className="text-amber-700 text-sm bg-amber-50 px-3 py-2 rounded-lg">
                Limited availability for this time. You’ll be added to the waiting list and we’ll contact you if a table becomes available.
              </p>
            )}
            {!canFitTableStandard && canFitVenueStandard && !mustWaitlistStandard && (
              <p className="text-blue-700 text-sm bg-blue-50 px-3 py-2 rounded-lg">
                We’ll arrange combined seating for your party. Our team will confirm your table when you arrive.
              </p>
            )}

            {submitError && (
              <p className="text-red-700 text-sm bg-red-50 px-3 py-2 rounded-lg">{submitError}</p>
            )}

            <button
              type="submit"
              disabled={useAdultsKids && partySizeStandard < 1}
              className="w-full py-3 px-4 bg-pink-500 text-white font-semibold rounded-lg hover:bg-pink-600 focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {mustWaitlistStandard ? 'Join waiting list' : 'Request reservation'}
            </button>
          </form>
        </div>
        <p className="text-center text-sm text-gray-500 mt-4">{businessName}</p>
      </div>
    </div>
  );
};

export default PublicBookingPage;
